import { getSlug } from './getSlug';

export const URL_SLUG_LIMIT = 100;
export const URL_DOMAIN_SLUG_LIMIT = 32;

// A regex that validates the allowed characters in a url slug
const URL_SLUG_REGEX = /^[-a-z0-9.+_]+$/;
const URL_DOMAIN_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

/**
 * ID and NAME tokens must begin with a letter ([A-Za-z]) and may be followed by any number of letters,
 * digits ([0-9]), hyphens ("-"), underscores ("_"), colons (":"), and periods (".").
 * https://www.w3.org/TR/html4/types.html
 *
 * GitBook differences:
 *   - We don't allow colons (":") and normalize them to dashes ("-")
 */
const URL_HASH_REGEX = /^[A-Za-z][-a-z0-9_.]*$/;

/**
 * RegExp matching disallowed extensions in `normalizeURLSlugBasename`.
 */
const DISALLOWED_EXTENSIONS_REGEX = /\.(md|json)$/;

/**
 * Suggest a path from a non-normalized string for a slug used as a basename.
 * Extensions like .md and .json are not allowed.
 *
 * WARNING: its output feeds render-time path resolution and cache keys, so keep it stable for existing valid paths.
 */
export function normalizeURLSlugBasename(str: string): string {
    const normalized = normalizeURLSlugInternal(str);
    const match = normalized.match(DISALLOWED_EXTENSIONS_REGEX);
    if (match) {
        return `${normalized.slice(0, -match[0].length)}-${match[1]}`;
    }
    return normalized;
}

/**
 * Suggest a path from a non-normalized string, such as user input.
 */
export function normalizeURLSlug(str: string): string {
    return normalizeURLSlugInternal(str);
}

/**
 * Normalize a slug to be used as a domain in a url.
 */
export function normalizeURLDomainSlug(str: string, maxModifier = 0): string {
    return normalizeURLSlugInternal(str, {
        min: 3,
        max: URL_DOMAIN_SLUG_LIMIT + maxModifier,
        allowPathChars: false,
    });
}

/**
 * Normalize a slug to be used as an anchor / ID in a dom.
 *
 * By default, ad-like patterns are rewritten (`ads` → `a-ds`) so ad-blocker cosmetic filters
 * don't hide the element. Disable `rewriteAdPatterns` for IDs explicitly chosen by the user,
 * which must be preserved as written.
 */
export function normalizeURLAnchor(
    str: string,
    options: { rewriteAdPatterns?: boolean } = {}
): string {
    const { rewriteAdPatterns = true } = options;
    // Avoid AdBlockers breaking the content
    const withoutAds = rewriteAdPatterns
        ? str.replace(/(^|\W)(ad)(vert|s|\W|$)(.*)/gi, '$1a-d$3$4')
        : str;
    let id = normalizeURLSlug(withoutAds)
        .replace(/[^A-Za-z0-9-_.]/g, '') // Remove all invalid characters
        .replace(/[^A-Za-z0-9]*$/, ''); // Remove trailing non alphanumeric characters ([A-Za-z])

    // Must begin with a letter ([A-Za-z]), so we strip the first character if it's not a letter
    // until we find one that is.
    while (id[0] !== undefined && !/[A-Za-z]/.test(id[0])) {
        // If it's a number, we prepend "id", but we preserve the number
        id = /[0-9]/.test(id[0]) ? `id-${id}` : id.slice(1);
    }

    // If the id is empty, we default to 'id'
    return id.length === 0 ? 'id' : id;
}

/**
 * Internal implementation of normalizeURLSlug.
 * The exported version doesn't accept any options because the callers should't be able to change them.
 */
function normalizeURLSlugInternal(
    str: string,
    options: {
        max?: number;
        min?: number;

        /** Should it allow characters that are not accepted in a domain (. and +) */
        allowPathChars?: boolean;
    } = {}
): string {
    const { min = 1, max = URL_SLUG_LIMIT, allowPathChars = true } = options;

    let path = validateURLSlugInternal({ str, allowPathChars })
        ? str
        : getSlug(str, allowPathChars);

    path = path || 'undefined';

    if (path.length < min) {
        path = path.padEnd(min, '0');
    }

    if (path.length > max) {
        // Slice it and normalize it again
        path = normalizeURLSlugInternal(path.slice(0, max), options);
    }

    if (!validateURLSlugInternal({ str: path, allowPathChars })) {
        throw new Error(`normalizeURLSlugInternal of "${str}" failed, got "${path}"`);
    }

    return path;
}

/**
 * Check if a string can be slugified.
 */
export function canBeSlugified(
    str: string,
    options?: {
        allowPathChars?: boolean;
    }
): boolean {
    return getSlug(str, options?.allowPathChars ?? true).length > 0;
}

/**
 * Validate a path slug basename.
 */
export function validateURLSlugBasename(str: string): boolean {
    return validateURLSlug(str) && !DISALLOWED_EXTENSIONS_REGEX.test(str);
}

/**
 * Validate a path slug.
 */
export function validateURLSlug(str: string): boolean {
    return validateURLSlugInternal({ str, allowPathChars: true });
}

/**
 * Validate a slub to be used as a domain in a url.
 */
export function validateURLDomainSlug(str: string): boolean {
    return (
        validateURLSlugInternal({ str, allowPathChars: false }) &&
        str.length <= URL_DOMAIN_SLUG_LIMIT
    );
}

/**
 * Normalize a slug to be used as an anchor / ID in a dom.
 */
export function validateURLAnchor(str: string): boolean {
    return URL_HASH_REGEX.test(str);
}

/**
 * Internal implementation of validateURLSlug.
 * The exported version doesn't accept an additional flag because the callers should't be able to change it.
 */
function validateURLSlugInternal({
    str,
    allowPathChars,
}: {
    str: string;
    allowPathChars: boolean;
}): boolean {
    return (
        (allowPathChars ? URL_SLUG_REGEX : URL_DOMAIN_SLUG_REGEX).test(str) &&
        str[0] !== '-' &&
        str[str.length - 1] !== '-'
    );
}
